import { useEffect, useRef, useState } from "react";
import {
  Stack, Title, Text, Button, Paper, Alert, Group, Table, Loader, Badge,
} from "@mantine/core";
import ResponsiveTable from "../components/ResponsiveTable";
import { IconDatabaseExport, IconDownload, IconInfoCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import {
  createDataExport, getDataExportStatus, listDataExports, downloadDataExport,
} from "../api";

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DataExportPage() {
  const { t, i18n } = useTranslation("Veft");
  const [exports, setExports] = useState(null);
  const [running, setRunning] = useState(false);
  const poll = useRef(null);

  const dateFmt = new Intl.DateTimeFormat(i18n.language === "de" ? "de-DE" : "en-GB", {
    dateStyle: "medium",
  });

  const refresh = () => listDataExports().then((data) => setExports(data ?? []));

  useEffect(() => {
    refresh();
    return () => poll.current && clearInterval(poll.current);
  }, []);

  const start = async () => {
    setRunning(true);
    try {
      const { jobId } = await createDataExport();
      notifications.show({ color: "blue", title: t("export_started"), message: t("export_started_msg") });

      poll.current = setInterval(async () => {
        try {
          const job = await getDataExportStatus(jobId);
          if (job.status === "success") {
            clearInterval(poll.current);
            setRunning(false);
            notifications.show({ color: "teal", title: t("export_ready"), message: t("export_ready_msg") });
            refresh();
          } else if (job.status === "failed") {
            clearInterval(poll.current);
            setRunning(false);
            notifications.show({ color: "red", title: t("export_failed"), message: t("export_failed_msg") });
          }
        } catch {
          /* transient poll error — keep trying */
        }
      }, 2500);
    } catch (e) {
      setRunning(false);
      // 409 = an export is already running for this account. The api wrapper throws a plain
      // Error whose message is prefixed "Conflict (409): …", so match either form.
      const already = e?.status === 409 || /\(409\)/.test(String(e?.message || ""));
      const msg = already ? t("export_already_running") : t("export_failed_msg");
      notifications.show({ color: "red", title: t("export_failed"), message: msg });
    }
  };

  if (exports === null) return <Group justify="center" p="xl"><Loader /></Group>;

  return (
    <Stack gap="lg" maw={760}>
      <Title order={3}>{t("export_title")}</Title>

      <Text c="dimmed" size="sm">{t("export_intro")}</Text>

      <Alert color="blue" icon={<IconInfoCircle size={18} />}>
        {t("export_retention_note")}
      </Alert>

      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" align="center">
          <Text fw={600}>{t("export_create_heading")}</Text>
          <Button
            leftSection={<IconDatabaseExport size={16} />}
            onClick={start}
            loading={running}
          >
            {t("export_start")}
          </Button>
        </Group>
        {running && (
          <Text c="dimmed" size="sm" mt="sm">{t("export_running_hint")}</Text>
        )}
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text fw={600}>{t("export_history")}</Text>
          {exports.length === 0 ? (
            <Text c="dimmed" size="sm">{t("export_empty")}</Text>
          ) : (
            <ResponsiveTable minWidth={560} verticalSpacing="xs" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("export_col_created")}</Table.Th>
                  <Table.Th>{t("export_col_contacts")}</Table.Th>
                  <Table.Th>{t("export_col_size")}</Table.Th>
                  <Table.Th>{t("export_col_expires")}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {exports.map((e) => {
                  const expired = new Date(e.expiresAt) < new Date();
                  return (
                    <Table.Tr key={e.id}>
                      <Table.Td>{dateFmt.format(new Date(e.createdAt))}</Table.Td>
                      <Table.Td>{e.candidateCount}</Table.Td>
                      <Table.Td>{formatBytes(e.sizeBytes)}</Table.Td>
                      <Table.Td>
                        {expired
                          ? <Badge color="gray" variant="light">{t("export_expired")}</Badge>
                          : dateFmt.format(new Date(e.expiresAt))}
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconDownload size={14} />}
                          disabled={expired}
                          onClick={() => downloadDataExport(e.id)}
                        >
                          {t("export_download")}
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </ResponsiveTable>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
